import React, { FC, useCallback } from 'react';
import { toast } from 'react-toastify';

import Button, { BUTTON_TYPES, BUTTON_WIDTHS } from 'buyside/components/presentation/Button/button';
import Link from 'buyside/components/presentation/Link/link';

import { employerProfileUrl, newResearchUrl } from 'buyside/util/urlHelper';

import {
  JoinCapIntroCurrentUserQuery,
  useCreateFundProfileMutation
} from 'interfaces/graphql-typings';

import styles from './heroStyles.scss';

const CurrentEmployer: FC<NonNullable<
  NonNullable<JoinCapIntroCurrentUserQuery['currentUser']>['currentEmployers']
>[number]> = ({ id: employerId, canExtendCapIntroAccess, employerProfile, name: employerName }) => {
  const [createFundProfile, { loading }] = useCreateFundProfileMutation();

  const handleCreateFundProfile = useCallback(
    () =>
      createFundProfile({ variables: { employerId } })
        .then(() => {
          window.location.href = employerProfileUrl(employerId);
        })
        .catch(error => {
          toast.error(error.message, { autoClose: 5000, theme: 'colored' });
        }),
    [createFundProfile, employerId]
  );

  const generateFundProfileButton = useCallback(() => {
    if (!employerProfile)
      return (
        <>
          <Button
            isLoading={loading}
            onClick={handleCreateFundProfile}
            text="Create & Publish Your Fund Profile"
            type={BUTTON_TYPES.PRIMARY}
            width={BUTTON_WIDTHS.FULL}
          />
          <p>
            Please create and publish your fund profile. <strong>One week</strong> of initial access
            to the Cap Intro platform will be granted once your profile is approved. Access is
            extended by up to 6 months whenever you or a colleague publish research associated with
            your fund.
          </p>
        </>
      );

    const { status } = employerProfile;

    if (status === 'draft')
      return (
        <>
          <Button
            text="Finish Publishing Your Fund Profile"
            type={BUTTON_TYPES.PRIMARY}
            url={employerProfileUrl(employerId)}
            width={BUTTON_WIDTHS.FULL}
          />
          <p>
            Please finish publishing your fund profile. <strong>One week</strong> of initial access
            to the Cap Intro platform will be granted once your profile is approved. Access is
            extended by up to 6 months whenever you or a colleague publish research associated with
            your fund.
          </p>
        </>
      );

    return (
      <>
        <Button
          text="View Your Fund Profile"
          type={BUTTON_TYPES.PRIMARY}
          url={employerProfileUrl(employerId)}
          width={BUTTON_WIDTHS.FULL}
        />
        {status === 'pending' ? (
          <span>
            Your fund profile is currently pending approval. <strong>One week</strong> of initial
            access to the Cap Intro platform will be granted once your profile is approved. Access
            is extended by up to 6 months whenever you or a colleague publish research associated
            with your fund.
          </span>
        ) : null}
        {status === 'hidden' && canExtendCapIntroAccess ? (
          <>
            <Button text="Submit Research" type={BUTTON_TYPES.MAJOR} url={newResearchUrl()} />
            <span>
              Your fund profile is currently hidden. Please submit research that's associated with
              your fund in order to reactivate your profile and access the Cap Intro platform.
            </span>
          </>
        ) : null}
      </>
    );
  }, [canExtendCapIntroAccess, employerProfile, employerId, loading, handleCreateFundProfile]);

  return (
    <div className={styles.heroCurrentEmployer}>
      <h4>{employerName}</h4>
      {generateFundProfileButton()}
      <span>
        Please contact{' '}
        <strong>
          <Link to="mailto:support@sumzero.com">support@sumzero.com</Link>
        </strong>{' '}
        if you have any questions.
      </span>
    </div>
  );
};
export default CurrentEmployer;
